import z from 'zod';
import { zoneRef, assetGroupRef } from './refs';
import { schedule } from './schedule';
import { datetime, url } from './zod';
import { AlarmSeverity } from './enums';
export var KioskConfigAlarmMode;
(function (KioskConfigAlarmMode) {
    KioskConfigAlarmMode["ZoneBased"] = "zoneBased";
    KioskConfigAlarmMode["CustomerBased"] = "customerBased";
})(KioskConfigAlarmMode || (KioskConfigAlarmMode = {}));
export var KioskAlarmSoundName;
(function (KioskAlarmSoundName) {
    KioskAlarmSoundName["Alarm"] = "alarm";
    KioskAlarmSoundName["AlarmClock"] = "alarmClock";
    KioskAlarmSoundName["AlarmRetro"] = "alarmRetro";
    KioskAlarmSoundName["Announcement"] = "announcement";
    KioskAlarmSoundName["Attention"] = "attention";
    KioskAlarmSoundName["BassPulse"] = "bassPulse";
    KioskAlarmSoundName["BeepBeep"] = "beepBeep";
    KioskAlarmSoundName["BeepBurglar"] = "beepBurglar";
    KioskAlarmSoundName["BeepContinuous"] = "beepContinuous";
    KioskAlarmSoundName["BeepHighSoft"] = "beepHighSoft";
    KioskAlarmSoundName["BeepIncremental"] = "beepIncremental";
    KioskAlarmSoundName["Bell"] = "bell";
    KioskAlarmSoundName["Car"] = "car";
    KioskAlarmSoundName["Chickens"] = "chickens";
    KioskAlarmSoundName["Chime"] = "chime";
    KioskAlarmSoundName["ChirpSoft"] = "chirpSoft";
    KioskAlarmSoundName["ClockMelody"] = "clockMelody";
    KioskAlarmSoundName["CountdownSonar"] = "countdownSonar";
    KioskAlarmSoundName["Doorbell"] = "doorbell";
    KioskAlarmSoundName["Escalate"] = "escalate";
    KioskAlarmSoundName["Fancy"] = "fancy";
    KioskAlarmSoundName["FastAndNervous"] = "fastAndNervous";
    KioskAlarmSoundName["Harp"] = "harp";
    KioskAlarmSoundName["IncomingPhone"] = "incomingPhone";
    KioskAlarmSoundName["Introduction"] = "introduction";
    KioskAlarmSoundName["Intrusive"] = "intrusive";
    KioskAlarmSoundName["Invasion"] = "invasion";
    KioskAlarmSoundName["Jungle"] = "jungle";
    KioskAlarmSoundName["LofiChords"] = "lofiChords";
    KioskAlarmSoundName["LoopLow"] = "loopLow";
    KioskAlarmSoundName["MelodySoft"] = "melodySoft";
    KioskAlarmSoundName["NotifySubtle"] = "notifySubtle";
    KioskAlarmSoundName["OnOff"] = "onOff";
    KioskAlarmSoundName["Orchestral"] = "orchestral";
    KioskAlarmSoundName["Pling"] = "pling";
    KioskAlarmSoundName["PlingHigh"] = "plingHigh";
    KioskAlarmSoundName["PlingLow"] = "plingLow";
    KioskAlarmSoundName["PluckSong"] = "pluckSong";
    KioskAlarmSoundName["Quick"] = "quick";
    KioskAlarmSoundName["Scanning"] = "scanning";
    KioskAlarmSoundName["Shake"] = "shake";
    KioskAlarmSoundName["Silence"] = "silence";
    KioskAlarmSoundName["SirenHigh"] = "sirenHigh";
    KioskAlarmSoundName["SirenLow"] = "sirenLow";
    KioskAlarmSoundName["Soft"] = "soft";
    KioskAlarmSoundName["Spaceship"] = "spaceship";
    KioskAlarmSoundName["Startup"] = "startup";
    KioskAlarmSoundName["ThreeTones"] = "threeTones";
    KioskAlarmSoundName["Tornado"] = "tornado";
    KioskAlarmSoundName["Wailing"] = "wailing";
    KioskAlarmSoundName["Wakeup"] = "wakeup";
})(KioskAlarmSoundName || (KioskAlarmSoundName = {}));
export const kioskIncidentCounterConfig = z.object({
    enabled: z.boolean().default(true),
    countFromDate: datetime.nullable(),
    threshold: z.number().min(0).default(0),
});
export const kioskHomepageConfig = z.object({
    incidentCounter: kioskIncidentCounterConfig,
    anonymous: z.boolean().default(true),
    logo: url.nullable(),
    // Filters out assets that are not inside the zones
    assetInsideZones: zoneRef.array().optional(),
});
export const kioskAlarmAcknowledgeConfig = z.object({
    enabled: z.boolean().default(true),
    pin: z.string().regex(/^[0-9]{4,6}$/).nullable(),
    messagePresets: z.string().array().nullish(),
});
export const kioskAlarmSoundConfig = z.object({
    enabled: z.boolean().default(true),
    volume: z.number().min(0).max(100).optional(),
    sound: z.nativeEnum(KioskAlarmSoundName).optional(),
    stopAfterSeconds: z.number().min(0).optional(),
    stopAfterTouch: z.boolean().optional(),
});
export const kioskAlarmFocusConfig = z.object({
    flash: z.boolean().default(true),
    sound: kioskAlarmSoundConfig.optional(),
});
export const kioskAlarmFocusConfigMapping = z.object({
    [AlarmSeverity.Red]: kioskAlarmFocusConfig.optional(),
    [AlarmSeverity.Orange]: kioskAlarmFocusConfig.optional(),
    [AlarmSeverity.Green]: kioskAlarmFocusConfig.optional(),
});
export const kioskAlarmConfig = z.object({
    enabled: z.boolean().default(true),
    audio: z.boolean().default(true),
    focus: kioskAlarmFocusConfigMapping.optional(),
    flash: z.boolean().default(true),
    showPositionLabels: z.boolean().default(true),
    anonymous: z.boolean().default(true),
    acknowledge: kioskAlarmAcknowledgeConfig.optional(),
    mode: z.nativeEnum(KioskConfigAlarmMode).optional(),
});
export const kioskAvailabilityConfig = z.object({
    enabled: z.boolean().default(true),
    pin: z.string().regex(/^[0-9]{4,6}$/).nullable(),
    showAppBanner: z.boolean().default(true),
    forceShowSearch: z.boolean().default(false),
    forceShowLabelFilter: z.boolean().default(false),
});
export const kioskHardwareConfig = z.object({
    screenBrightness: z.number().min(0).max(100).nullable(),
});
export const kioskConfigExposeLabel = z.object({
    ...assetGroupRef.shape,
    countLabel: z.boolean().optional(),
});
export const kioskConfigOutput = z.object({
    managed: z.boolean().default(false),
    zone: zoneRef.nullable(),
    exposeAssets: assetGroupRef.array(),
    exposeLabels: kioskConfigExposeLabel.array(),
    countAssets: assetGroupRef.array(),
    schedule: schedule.nullable(),
    homepage: kioskHomepageConfig,
    alarm: kioskAlarmConfig,
    manageAvailability: kioskAvailabilityConfig,
    hardware: kioskHardwareConfig.optional(),
});
export const kioskConfig = z.object({
    ...kioskConfigOutput.shape,
    homepage: z.object({
        ...kioskHomepageConfig.shape,
        incidentCounter: kioskIncidentCounterConfig.partial(),
    }).partial(),
    alarm: z.object({
        ...kioskAlarmConfig.shape,
        acknowledge: kioskAlarmAcknowledgeConfig.partial(),
    }).partial(),
    manageAvailability: kioskAvailabilityConfig.partial(),
    hardware: kioskHardwareConfig.partial(),
}).partial();
export const defaultKioskConfig = kioskConfigOutput.omit({
    zone: true,
});
